import * as React from 'react';
import { useSelector } from 'react-redux';

import { ImageAsDocumentMessage } from '../../components/ImageAsDocumentMessage';
import { currentOpenChatIDSelector } from '../../selectors';
import { TImageFileAsDocument } from '../../types/messageFiles';
import { TMessage, TOutcomingStatus } from '../../types/messages';
import { formatBytes } from '../../utils/formatBytes';
import { getFileSrc } from '../../utils/getFileSrc';
import { ChatImageContainer } from '../ChatImageContainer';
import { ChatImageDraftContainer } from '../ChatImageDraftContainer';
import { useImageAsDocument } from '../ChatMessageContainer/hooks';

type TImageAsDocumentMessageProps = {
  message: TMessage & { status: TOutcomingStatus };
  messageId: string;
};

export const ImageAsDocumentMessageContainer: React.FC<TImageAsDocumentMessageProps> = ({ message, messageId }) => {
  const { files, isOutcoming, status } = message;

  const chatId = useSelector(currentOpenChatIDSelector);

  const { imageAsDocumentDraft, imageAsDocument } = useImageAsDocument(messageId, files?.imagesAsDocuments?.[0]);

  const image = React.useMemo(() => {
    if (imageAsDocumentDraft) {
      return (
        <ChatImageDraftContainer
          file={imageAsDocumentDraft.file}
          messageId={messageId}
          cancellationToken={imageAsDocumentDraft.cancellationToken}
          messageStatus={status}
        />
      );
    }

    return <ChatImageContainer image={imageAsDocument as TImageFileAsDocument} withSave />;
  }, [imageAsDocument, imageAsDocumentDraft, messageId, status]);

  const src = React.useMemo(() => {
    if (imageAsDocument?.uid) {
      return getFileSrc(imageAsDocument.uid, chatId);
    }

    return null;
  }, [chatId, imageAsDocument?.uid]);

  const name = React.useMemo(() => {
    return imageAsDocument?.name || imageAsDocumentDraft?.file?.name || '';
  }, [imageAsDocument?.name, imageAsDocumentDraft?.file?.name]);

  const size = React.useMemo(() => {
    const sizeNumber = imageAsDocument?.size || imageAsDocumentDraft?.file?.size;

    return formatBytes(sizeNumber);
  }, [imageAsDocument?.size, imageAsDocumentDraft?.file?.size]);

  if (!imageAsDocumentDraft && !imageAsDocument) {
    return null;
  }

  return <ImageAsDocumentMessage imageNode={image} name={name} size={size} src={src} isOutcoming={isOutcoming} />;
};
